import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Settings2, CreditCard, Loader, Save, Wand2, Printer, Coins, Percent, Power, AlertTriangle} from "lucide-react";
import toast from "react-hot-toast";
import usePermissions from "../../hooks/usePermissions";
import secureStorage from "../../utils/secureStorage";
import FormListbox from "../Common/FormListbox";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {printLoyaltyCard} from "./LoyaltyBarcode";
import {Field, Section, inputClass as formInputClass} from "../../ClientsTable/clientFormUI";
import {
    PageHeader, HeaderAction, SearchInput, ToolbarSelect, ListLoader, ListEmpty, ListCard, thClass, mobileListClass, mobileRowClass, ActiveFilters, RowActions, Pagination
} from "../../stock/StockUI";
import {
    CARD_STATUS_LABELS,
    apiErrorMessage,
    fetchDiscountOptions,
    fetchLoyaltyCards,
    fetchLoyaltyProgram,
    generateMissingLoyaltyCards,
    updateLoyaltyProgram,
} from "./loyaltyApi";
import Button from '../Common/Button';

const TABS = [
    {key: "program", label: "Programme", Icon: Settings2},
    {key: "cards", label: "Cartes", Icon: CreditCard},
];

const MODE_OPTIONS = [
    {key: "off", label: "Désactivé", description: "Pas de programme de fidélité.", Icon: Power},
    {key: "points", label: "Points", description: "Le client cumule des points à chaque achat et les utilise plus tard.", Icon: Coins},
    {key: "remise", label: "Remise", description: "Le porteur de la carte reçoit automatiquement une remise.", Icon: Percent},
];

const STATUS_CLASSES = {
    active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    replaced: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

const StatusBadge = ({status}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLASSES[status]}`}>
        {CARD_STATUS_LABELS[status]}
    </span>
);

const inputClass = `${formInputClass(false)} disabled:opacity-60`;
// Narrow input sitting inside a sentence ("1 point pour [ ] DH")
const inlineInputClass = inputClass.replace("w-full", "w-28");

const PROGRAM_FORM_ID = "loyalty-program-form";

const SectionTitle = ({children}) => (
    <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100">{children}</h2>
);

// ─── Programme ───────────────────────────────────────────────────────────────

const ProgramTab = ({canManage}) => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(null);

    const {data: program, isLoading, isError} = useQuery({
        queryKey: ["loyalty-program"],
        queryFn: fetchLoyaltyProgram,
    });
    const {data: discounts = []} = useQuery({
        queryKey: ["loyalty-discount-options"],
        queryFn: fetchDiscountOptions,
    });

    useEffect(() => {
        if (program) {
            setForm({
                mode: program.mode,
                dh_per_point: program.dh_per_point,
                redeem_points: program.redeem_points,
                redeem_value: program.redeem_value,
                min_redeem_points: program.min_redeem_points,
                points_expiry_months: program.points_expiry_months ?? "",
                discount_id: program.discount_id,
                max_days_since_last_visit: program.max_days_since_last_visit ?? "",
            });
        }
    }, [program]);

    const saveMutation = useMutation({
        mutationFn: updateLoyaltyProgram,
        onSuccess: (saved) => {
            queryClient.setQueryData(["loyalty-program"], saved);
            toast.success("Programme de fidélité enregistré");
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'enregistrer le programme")),
    });

    if (isLoading || (!form && !isError)) {
        return <ListLoader label="Chargement du programme..."/>;
    }
    if (isError) {
        return <p className="text-sm text-red-500 p-6 text-center">Impossible de charger le programme de fidélité.</p>;
    }

    const set = (field) => (e) => setForm((prev) => ({...prev, [field]: e.target.value}));

    const handleSave = (e) => {
        e.preventDefault();
        const payload = {mode: form.mode};
        if (form.mode === "points") {
            Object.assign(payload, {
                dh_per_point: Number(form.dh_per_point),
                redeem_points: Number(form.redeem_points),
                redeem_value: Number(form.redeem_value),
                min_redeem_points: Number(form.min_redeem_points),
                points_expiry_months: form.points_expiry_months === "" ? null : Number(form.points_expiry_months),
            });
        }
        if (form.mode === "remise") {
            payload.discount_id = form.discount_id || null;
            payload.max_days_since_last_visit =
                form.max_days_since_last_visit === "" ? null : Number(form.max_days_since_last_visit);
        }
        saveMutation.mutate(payload);
    };

    // Worked example shown under the points rules.
    const dhPerPoint = Number(form.dh_per_point) || 0;
    const redeemPoints = Number(form.redeem_points) || 0;
    const redeemValue = Number(form.redeem_value) || 0;
    const exampleSpend = dhPerPoint > 0 && redeemPoints > 0 ? dhPerPoint * redeemPoints : null;

    return (
        <form id={PROGRAM_FORM_ID} onSubmit={handleSave}
              className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8 w-full">
            <div className="space-y-4">
                <div>
                    <SectionTitle>Type de fidélité</SectionTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        S'applique à toutes les caisses du commerce. Les caisses reçoivent ces règles à leur prochaine synchronisation.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    {MODE_OPTIONS.map(({key, label, description, Icon}) => {
                        const selected = form.mode === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                disabled={!canManage}
                                onClick={() => setForm((prev) => ({...prev, mode: key}))}
                                className={`text-left p-4 rounded-lg border transition disabled:cursor-not-allowed ${
                                    selected
                                        ? "border-primary bg-primary/5"
                                        : "border-gray-200 dark:border-gray-600 hover:border-primary"
                                }`}
                            >
                                <Icon className={`w-5 h-5 mb-2 ${selected ? "text-primary" : "text-gray-400"}`}/>
                                <div className="font-medium text-gray-800 dark:text-white">{label}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {form.mode === "points" && (
                <Section>
                    <SectionTitle>Règles des points</SectionTitle>

                    <Field label="Gain de points">
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span>1 point pour</span>
                            <input type="number" min="0.01" step="0.01" required disabled={!canManage}
                                   value={form.dh_per_point} onChange={set("dh_per_point")} className={inlineInputClass}/>
                            <span>DH dépensés</span>
                        </div>
                    </Field>

                    <Field label="Valeur des points">
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input type="number" min="1" step="1" required disabled={!canManage}
                                   value={form.redeem_points} onChange={set("redeem_points")} className={inlineInputClass}/>
                            <span>points =</span>
                            <input type="number" min="0.01" step="0.01" required disabled={!canManage}
                                   value={form.redeem_value} onChange={set("redeem_value")} className={inlineInputClass}/>
                            <span>DH de remise</span>
                        </div>
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <Field id="min_redeem_points" label="Minimum pour utiliser les points">
                            <input id="min_redeem_points" type="number" min="0" step="1" required disabled={!canManage}
                                   value={form.min_redeem_points} onChange={set("min_redeem_points")} className={inputClass}/>
                        </Field>
                        <Field id="points_expiry_months" label="Expiration des points (mois)">
                            <input id="points_expiry_months" type="number" min="0" step="1" disabled={!canManage} placeholder="Jamais"
                                   value={form.points_expiry_months} onChange={set("points_expiry_months")} className={inputClass}/>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Laisser vide : les points n'expirent jamais.</p>
                        </Field>
                    </div>

                    {exampleSpend !== null && redeemValue > 0 && (
                        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm text-gray-700 dark:text-gray-300">
                            Exemple : un client qui dépense {exampleSpend.toLocaleString("fr-FR")} DH cumule {redeemPoints} points,
                            soit {redeemValue.toLocaleString("fr-FR")} DH de remise
                            ({((redeemValue / exampleSpend) * 100).toLocaleString("fr-FR", {maximumFractionDigits: 2})} % de ses achats).
                        </div>
                    )}
                </Section>
            )}

            {form.mode === "remise" && (
                <Section>
                    <SectionTitle>Remise des porteurs de carte</SectionTitle>
                    <div>
                        <FormListbox
                            label="Remise appliquée automatiquement"
                            options={discounts}
                            value={form.discount_id}
                            onChange={(id) => setForm((prev) => ({...prev, discount_id: id}))}
                            placeholder="Choisir une remise"
                            required
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Dès qu'un client avec une carte active est associé à une vente, cette remise s'applique à tous les articles.{" "}
                            <Link to="/NewRemiseForm" className="text-primary hover:underline">Créer une remise</Link>
                        </p>
                    </div>
                    <Field label="Condition de dernière visite">
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span>Uniquement si la dernière visite date de moins de</span>
                            <input type="number" min="1" step="1" disabled={!canManage} placeholder="—"
                                   value={form.max_days_since_last_visit} onChange={set("max_days_since_last_visit")}
                                   className={inlineInputClass}/>
                            <span>jours</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Laisser vide : la remise s'applique à chaque visite. Un client sans visite précédente n'en bénéficie pas.
                        </p>
                    </Field>
                </Section>
            )}

            {saveMutation.isPending && (
                <p className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader className="w-4 h-4 animate-spin text-primary"/> Enregistrement…
                </p>
            )}
        </form>
    );
};

// ─── Cartes ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const CardsTab = ({canManage}) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [confirmGenerate, setConfirmGenerate] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const {data, isLoading, isError, isFetching} = useQuery({
        queryKey: ["loyalty-cards", debouncedSearch, status, page],
        queryFn: () => fetchLoyaltyCards({page, itemsPerPage: PAGE_SIZE, search: debouncedSearch, status}),
        placeholderData: keepPreviousData,
    });

    const generateMutation = useMutation({
        mutationFn: generateMissingLoyaltyCards,
        onSuccess: ({created}) => {
            toast.success(created > 0 ? `${created} carte(s) créée(s)` : "Tous les clients ont déjà une carte");
            queryClient.invalidateQueries({queryKey: ["loyalty-cards"]});
            queryClient.invalidateQueries({queryKey: ["loyalty-client-cards"]});
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible de générer les cartes")),
    });

    const handlePrint = async (card) => {
        const account = await secureStorage.getItem("selectedAccount");
        if (!printLoyaltyCard({code: card.code, clientName: card.client_name, shopName: account?.name || ""})) {
            toast.error("Autorisez les fenêtres pop-up pour imprimer la carte");
        }
    };

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const missing = data?.clients_without_card || 0;

    const cardActions = (card) => card.status === "replaced" ? [] : [
        {label: "Imprimer la carte", icon: Printer, onClick: () => handlePrint(card)},
    ];

    return (
        <div className="max-w-full">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Rechercher par code, nom ou téléphone..."/>
                <ToolbarSelect
                    label="Statut"
                    value={status}
                    onChange={(value) => {
                        setStatus(value);
                        setPage(1);
                    }}
                >
                    <option value="">Tous les statuts</option>
                    {Object.entries(CARD_STATUS_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </ToolbarSelect>
            </div>
            <ActiveFilters
                onClearAll={() => { setSearch(""); setDebouncedSearch(""); setStatus(""); setPage(1); }}
                chips={[
                    debouncedSearch && {key: "search", label: `Recherche: ${debouncedSearch}`, onRemove: () => { setSearch(""); setDebouncedSearch(""); setPage(1); }},
                    status && {key: "status", label: `Statut: ${CARD_STATUS_LABELS[status] || status}`, onRemove: () => { setStatus(""); setPage(1); }},
                ].filter(Boolean)}
            />

            {missing > 0 && canManage && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                    <p className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0"/>
                        {missing} client(s) n'ont pas encore de carte de fidélité.
                    </p>
                    <Button variant="primary" size="sm" icon={Wand2} loading={generateMutation.isPending}
                            onClick={() => setConfirmGenerate(true)}>
                        Générer les cartes manquantes
                    </Button>
                </div>
            )}

            {isLoading ? (
                <ListLoader label="Chargement des cartes..."/>
            ) : (
                <ListCard>
                    {isError ? (
                        <p className="text-sm text-red-500 p-6 text-center">Impossible de charger les cartes.</p>
                    ) : items.length === 0 ? (
                        <ListEmpty icon={CreditCard} title="Aucune carte trouvée"
                                   text="Aucune carte ne correspond à votre recherche."/>
                    ) : (<div className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
                        {/* Desktop Table */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Code</th>
                                    <th className={thClass}>Client</th>
                                    <th className={thClass}>Téléphone</th>
                                    <th className={thClass}>Statut</th>
                                    <th className={thClass}>Créée le</th>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {items.map((card) => (
                                    <tr key={card.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{card.code}</td>
                                        <td className="px-6 py-4 text-sm">
                                            <Link to={`/clients/edit/${card.client_id}`} className="font-medium text-primary hover:underline">
                                                {card.client_name || "Sans nom"}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {card.client_phone || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={card.status}/></td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {new Date(card.created_at).toLocaleDateString("fr-FR")}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {cardActions(card).length > 0 && <RowActions actions={cardActions(card)}/>}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                        <div className={mobileListClass}>
                            {items.map((card) => (
                                <div key={card.id} className={mobileRowClass}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-mono text-base font-semibold text-gray-900 dark:text-gray-100">{card.code}</p>
                                            <Link to={`/clients/edit/${card.client_id}`} className="text-sm font-medium text-primary hover:underline">
                                                {card.client_name || "Sans nom"}
                                            </Link>
                                            {card.client_phone && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{card.client_phone}</p>
                                            )}
                                        </div>
                                        {cardActions(card).length > 0 && <RowActions actions={cardActions(card)}/>}
                                    </div>
                                    <div className="flex items-center justify-between mt-3">
                                        <StatusBadge status={card.status}/>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Créée le {new Date(card.created_at).toLocaleDateString("fr-FR")}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>)}
                </ListCard>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <Pagination page={page} totalPages={totalPages} onPage={setPage} summary={`${total} carte(s)`}/>
            )}

            {confirmGenerate && (
                <ConfirmationDialog
                    title="Générer les cartes manquantes ?"
                    message={`Une carte de fidélité sera créée pour ${missing} client(s) qui n'en ont pas.`}
                    confirmLabel="Générer"
                    cancelLabel="Annuler"
                    onCancel={() => setConfirmGenerate(false)}
                    onConfirm={() => {
                        setConfirmGenerate(false);
                        generateMutation.mutate();
                    }}
                />
            )}
        </div>
    );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const LoyaltyPage = () => {
    const {canUpdate} = usePermissions();
    const canManage = canUpdate("loyalty");
    const [activeTab, setActiveTab] = useState("program");

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <PageHeader
                title="Fidélité"
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                actions={activeTab === "program" && canManage && (
                    <HeaderAction icon={Save} label="Enregistrer"
                                  onClick={() => document.getElementById(PROGRAM_FORM_ID)?.requestSubmit()}/>
                )}
            />

            {activeTab === "program" && <ProgramTab canManage={canManage}/>}
            {activeTab === "cards" && <CardsTab canManage={canManage}/>}
        </div>
    );
};

export default LoyaltyPage;
